import { Component, HostListener, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { CreateProjectService } from '../services/create-project.service';
import { ManageProjectService } from '../services/manage-project.service';
import { NotificationService } from '../services/notification.service';
import { RoleViewService } from '../services/role-view.service';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { ADMIN_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';

type CnraItem = {
  cnra_workday_id: string;
  cnra_name: string;
  cnra_email: string;
};
@Component({
  selector: 'select-project-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    TutorialVideoWidgetComponent
  ],
  templateUrl: './select-project-tab.component.html',
  styleUrl: './select-project-tab.component.css',
})
export class SelectProjectTabComponent implements OnInit {
  readonly adminTutorialSequence: TutorialVideoStep[] = ADMIN_TUTORIAL_FLOW;
  @Input() projectForm!: FormGroup;
  @Input() isCopyMode = false;
  naming = 'standard';
  scrHeight = '';
  namingType = 'standard';
  minYear: number = new Date().getFullYear();
  maxYear: number = this.minYear + 10;
  minDate: Date = new Date();
  cnraSearchText = '';
  cnraResults: CnraItem[] = [];
  cnraShowList = false;
  cnraQueryIsEmailLike = false;
  cnraSearched = false;
  cnraLoading = false;
  selectedCnra?: CnraItem;
  private debounceTimer?: any;
  private lastFetchedQuery = '';
  private lastFetchedPayload: CnraItem[] | null = null; // populated once via API in ngOnInit
  private lastRequestedQuery = '';
  isLoading = false;
  userRole = '';
  workDayId: any;
  selectedProject: any;
  selectedProjectColor = '';
  isEditingProject = false;
  projects: any[] = [];
  superAdminRole = '';
  
  constructor(private readonly fb: FormBuilder, private readonly createProjectService: CreateProjectService, private readonly manageProjectService: ManageProjectService,
    private readonly notificationService: NotificationService,private readonly roleViewService: RoleViewService
  ) { this.getScreenSize(); }
  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: Event) {
    const offset = window.innerWidth <= 1000 ? 305 : 255;
    this.scrHeight = `${window.innerHeight - offset}px`;
  }

  ngOnInit(): void {
  this.workDayId = this.createProjectService.getWorkdayId();
  this.selectedProject = this.createProjectService.getSelectedProject();

  this.naming = this.selectedProject ? this.selectedProject.naming : 'standard';
  this.cnraSearchText = this.selectedProject ? this.selectedProject.contactPerson : '';

  console.info('this.selectedProject', this.selectedProject);

  const addIfMissing = (name: string, control: FormControl) => {
    if (!this.projectForm.get(name)) {
      this.projectForm.addControl(name, control);
    }
  };

  if (this.projectForm) {
    addIfMissing('projectMasterId', new FormControl('', Validators.required));
    addIfMissing('modelYear', new FormControl(''));
    addIfMissing('modelCode', new FormControl(''));
    addIfMissing('projectCode', new FormControl(''));
    addIfMissing('projectName', new FormControl(''));
    addIfMissing('naming', new FormControl('standard', Validators.required));
    addIfMissing('documentNumber', new FormControl('', [Validators.required, Validators.pattern(/^[A-Z0-9-]+$/)]));
    addIfMissing('contactPerson', new FormControl('', Validators.required));

    this.projectForm.get('projectMasterId')?.setValidators(Validators.required);
    this.projectForm.get('projectMasterId')?.updateValueAndValidity({ emitEvent: false });
  } else {
    this.projectForm = this.fb.group({
      modelYear: [''],
      modelCode: [''],
      projectCode: [''],
      projectMasterId: ['', Validators.required],
      projectName: [{ value: '' }],
      naming: ['standard', Validators.required],
      documentNumber: ['', [Validators.required, Validators.pattern(/^[A-Z0-9-]+$/)]],
      contactPerson: ['', Validators.required]
    });
  }

  this.projectForm.get('contactPerson')?.updateValueAndValidity({ emitEvent: false });

  if (!this.projectForm.get('contactPersonSelected')) {
    this.projectForm.addControl('contactPersonSelected', new FormControl(false));
  }

  this.createProjectService.fetchCnraDetails(this.workDayId).subscribe({
    next: (res) => {
      const payload: CnraItem[] = res?.payload || [];
      this.lastFetchedPayload = payload;
      this.lastFetchedQuery = '';
      console.info('Initial CNRA fetch successful. Records:', payload.length);
    }
  });

  this.loadProjects();
}

  loadProjects(): void {
    // Sample existing projects
    this.manageProjectService.fetchProject().subscribe({ next: res => {
      this.projects = res.payload.filter((item: any) => !item.is_deleted);
      // Sort projects by color code
      this.projects.sort((a, b) => {
        const colorA = (a.color_code || '').toUpperCase();
        const colorB = (b.color_code || '').toUpperCase();
        return colorA.localeCompare(colorB);
      });
      this.manageProjectService.setFetchProject(this.projects);
      const currentProjectId = this.projectForm?.get('projectMasterId')?.value;
      if (currentProjectId) {
        this.onProjectNameSelection(String(currentProjectId));
      }
    }, error: (error: any) => {
        console.trace('Error loading collections:', error);
        this.notificationService.showError('Error loading projects from server');
      }
    });
  }
  onProjectNameSelection(projectMasterId: string): void {
    const selected = this.projects.find(p => p.project_id === Number(projectMasterId));
    console.info('Selected project object:', selected);
    if (selected) {
      this.projectForm.patchValue({
        projectMasterId: selected.project_id,
        modelYear: selected.model_year,
        modelCode: selected.model_code,
        projectCode: selected.project_code,
        projectName: selected.project_name
      });
      this.selectedProjectColor = selected.color_code || '';
    } else {
      this.selectedProjectColor = '';
    }
    console.info('projectNameControl', this.projectNameControl?.value);
  }

  selectProjectCard(project: any): void {
    this.projectForm.patchValue({
      projectMasterId: project.project_id
    });
    this.projectMasterIdControl?.markAsTouched();
    this.onProjectNameSelection(String(project.project_id));
    this.isEditingProject = false;
  }

  toggleEditProject(): void {
    this.isEditingProject = !this.isEditingProject;
  }

  getSelectedProjectColorName(): string {
    const selectedProjectId = this.projectMasterIdControl?.value;
    if (!selectedProjectId || !this.projects) {
      return 'N/A';
    }
    const project = this.projects.find(p => p.project_id === selectedProjectId);
    return project?.color_name || 'N/A';
  }





  resetFormFields(): void {
    // Reset all the fields in the form
    this.projectForm.get('modelYear')?.setValue('');
    this.projectForm.get('modelCode')?.setValue('');
    this.projectForm.get('projectCode')?.setValue('');
    this.projectForm.get('projectName')?.setValue('');
    // Optionally, reset the form controls (if you want to clear validation errors as well)
    this.projectForm.get('modelYear')?.markAsPristine();
    this.projectForm.get('modelCode')?.markAsPristine();
    this.projectForm.get('projectCode')?.markAsPristine();
    this.projectForm.get('projectName')?.markAsPristine();
  }

  get modelYearInput() {
    return this.projectForm.get('modelYear');
  }

  get modelCode() {
    return this.projectForm.get('modelCode');
  }

  get projectCode() {
    return this.projectForm.get('projectCode');
  }

  get projectNameControl() {
    return this.projectForm.get('projectName');
  }

  get projectName() {
    return this.projectForm.get('projectName');
  }

  get projectMasterIdControl(): AbstractControl | null {
    return this.projectForm.get('projectMasterId');
  }

  get contactPersonControl(): AbstractControl | null {
    return this.projectForm.get('contactPerson');
  }

  onCnraInput(text: string) {
    this.cnraSearchText = text;
    this.cnraShowList = text.length >= 1;
    // mark that a search hasn't completed for this input yet
    this.cnraSearched = false;
    // clear loading state when user changes input
    this.cnraLoading = false;
    this.selectedCnra = undefined; 
    // mark that the user has NOT selected a suggestion
    if (this.projectForm?.get('contactPersonSelected')) {
      this.projectForm.get('contactPersonSelected')?.setValue(false, { emitEvent: false });
      this.projectForm.get('contactPerson')?.updateValueAndValidity({ emitEvent: false });
    }
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.searchCnra(text.trim()), 300); // Set new debounce timeout
  }

  onCnraBlur() {
    setTimeout(() => {
      this.cnraShowList = false;
      this.cnraResults = [];
      this.cnraSearched = false;
      this.cnraLoading = false;
    }, 150);
  }

  selectCnra(item: any) {
    this.selectedCnra = item;
    const inputIsEmail = !!this.cnraQueryIsEmailLike;
    this.cnraSearchText = inputIsEmail ? item.cnra_email : item.cnra_name;
    this.projectForm.patchValue({ contactPerson: item.cnra_name });
    // mark that the user explicitly selected a contact from the list
    if (this.projectForm?.get('contactPersonSelected')) {
      this.projectForm.get('contactPersonSelected')?.setValue(true, { emitEvent: false });
      this.projectForm.get('contactPerson')?.updateValueAndValidity({ emitEvent: false });
    }
    this.cnraShowList = false;
    this.cnraResults = [];
  }

  private searchCnra(query: string) {
    // No search for short queries
    if (query.length < 1) {
      this.cnraResults = [];
      return;
    }

    const raw = query; // keep original for UI/email-detection
    this.lastRequestedQuery = raw;

    // Use the payload fetched in ngOnInit to perform client-side filtering
    const payload = this.lastFetchedPayload || [];
    if (!payload.length) {
      this.cnraResults = [];
      this.cnraSearched = true;
      this.cnraLoading = false;
      return;
    }

    this.cnraLoading = true;
    this.applyCnraFilter(payload, raw);
  }

  private applyCnraFilter(payload: CnraItem[], query: string) {
    const qRaw = query.trim();
    const q = qRaw.toLowerCase();
    const qTokens = q.split(/\s+/).filter(Boolean);
    let isEmailLike = false;
    if (qRaw.includes('@')) {
      isEmailLike = true;
    } else if (qRaw.endsWith('.')) {
      const beforeDot = qRaw.slice(0, -1).split('.')[0] || '';
      const token = beforeDot.toLowerCase();
      const firstNameMatch = payload.some(item => {
        const first = ((item.cnra_name || '').split(/\s+/)[0] || '').toLowerCase();
        return first === token;
      });
      isEmailLike = firstNameMatch;
    } else {
      isEmailLike = false;
    }
    this.cnraQueryIsEmailLike = isEmailLike;
    this.cnraResults = payload.filter(item => {
      const name = (item.cnra_name || '').toLowerCase();
      const email = (item.cnra_email || '').toLowerCase();

      if (isEmailLike) {
        const qEmail = q.replaceAll(/^@/g, '').replaceAll(/\.+$/g,'');
        // For email-like search, still allow substring match in email
        return email.includes(qEmail);
      }

      if (!qTokens.length) {
        return false;
      }
      return name.startsWith(q);
    });
    // indicate that filtering/search has completed for UI messaging
    this.cnraSearched = true;
    this.cnraLoading = false;
  }

  buildSubmitPayload() {
    const { contactPerson } = this.projectForm.value;
    const { cnra_name, cnra_email, cnra_workday_id } = this.selectedCnra || {};
    return {
      contact_person_name: cnra_name || contactPerson,
      contact_person_email: cnra_email || null,
      contact_person_workday_id: cnra_workday_id || null
    };
  }

  onSubmit() {
    if (this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();
      return;
    }
    const payload = this.buildSubmitPayload();
    console.info('Submit payload:', payload);
  }

  toggleNamingFields(namingType: string): void {
    if (namingType === 'standard') {
      this.projectForm.get('modelYear')?.enable();
      this.projectForm.get('modelCode')?.enable();
      this.projectForm.get('projectCode')?.enable();
    
    } else {
      // Disable fields specific to custom naming
      this.projectForm.get('modelYear')?.disable();
      this.projectForm.get('modelCode')?.disable();
      this.projectForm.get('projectCode')?.disable();
      this.projectForm.get('projectName')?.enable();
    }
  }
  get projectNamePlaceholder(): string {
  return this.naming === 'standard'
    ? 'Will be auto-generated...'
    : 'Enter custom project name';
  }


  onInputValueChange(_event: Event): void {
    const yearCtrl = this.projectForm.get('modelYear');
    const modelCodeCtrl = this.projectForm.get('modelCode');
    const projectCodeCtrl = this.projectForm.get('projectCode');
    const documentNumberCtrl = this.projectForm.get('documentNumber');

    // Sanitize inputs (keep these to enforce UX)
    const rawYear = (yearCtrl?.value ?? '').toString();
    const sanitizedYear = rawYear.replaceAll(/\D+/g, '').slice(0, 4);
    if (sanitizedYear !== rawYear) {
      yearCtrl?.setValue(sanitizedYear, { emitEvent: false });
    }

    const rawModelCode = (modelCodeCtrl?.value ?? '').toString();
    // Normalize to uppercase and strip invalid characters
    const normalized = rawModelCode.toUpperCase().replaceAll(/[^A-Z0-9]/g, '');
    // Extract up to 3 leading digits
    const digitMatch = normalized.match(/^\d{1,3}/);
    const digits = digitMatch ? digitMatch[0] : '';
    // Remaining characters should be letters only — keep only the first letter (single alphabet requirement)
    const restAll = normalized.slice(digits.length).replaceAll(/[^A-Z]/g, '');
    const rest = restAll ? restAll[0] : '';
    const finalModelCode = digits + rest;
    if (finalModelCode !== rawModelCode) {
      modelCodeCtrl?.setValue(finalModelCode, { emitEvent: false });
      // Force recompute validity immediately
      modelCodeCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    const rawProjectCode = (projectCodeCtrl?.value ?? '').toString();
    const sanitizedProjectCode = rawProjectCode.replaceAll(/[^a-zA-Z0-9]+/g, '').toUpperCase();
    if (sanitizedProjectCode !== rawProjectCode) {
      projectCodeCtrl?.setValue(sanitizedProjectCode, { emitEvent: false });
    }

    // Sanitize documentNumber (A–Z, 0–9, hyphen)
    const rawDocNum = (documentNumberCtrl?.value ?? '').toString();
    const sanitizedDocNum = rawDocNum.replaceAll(/[^a-zA-Z0-9-]+/g, '').toUpperCase();
    if (sanitizedDocNum !== rawDocNum) {
      documentNumberCtrl?.setValue(sanitizedDocNum, { emitEvent: false });
    }

    const yearValid = !!yearCtrl && yearCtrl.valid;
    const modelCodeValid = !!modelCodeCtrl && modelCodeCtrl.valid;
    const projectCodeValid = !!projectCodeCtrl && projectCodeCtrl.valid;

    if (!this.projectMasterIdControl?.value && yearValid && modelCodeValid && projectCodeValid) {
      const name = `${sanitizedYear}-${finalModelCode}-${sanitizedProjectCode}`.toUpperCase();
      this.projectForm.get('projectName')?.setValue(name, { emitEvent: false });
    } 
    
  }
}
